import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    FlowArtifact,
    FlowComponent,
    FlowComponentsPanelState,
    FlowManifest,
    FlowSection,
    ENTRY_COMPONENT_NAME,
    OUTPUT_COMPONENT_NAME
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {
    CloudComponent,
    CloudComponentId,
    ResolvedCloudArtifact
} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {NameUtil} from '@mintdata-wsp/common/util/NameUtil';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import bind from 'bind-decorator';
import {action} from 'mobx';

export class FlowComponentsPanelController
{
    public onCloudArtifactsLoaded()
    {
        const cloudArtifacts = controllers.ui.artifacts.getCloudArtifacts();
        const manifest = this._computeInitialManifest(cloudArtifacts);

        this._setManifest(manifest);
    }

    @action.bound
    private _setManifest(manifest:FlowManifest):void
    {
        this._getState().manifest = manifest;
    }

    public getManifest():FlowManifest | null
    {
        return this._getState().manifest;
    }

    @action.bound
    public clearFilterString()
    {
        this.setFilterString(null);
    }

    @action.bound
    public setFilterString(filterString:string):void
    {
        const state = this._getState();
        state.filterString = filterString;

        const cloudArtifacts = controllers.ui.artifacts.getCloudArtifacts();
        const manifest = this.getManifest();

        if( !manifest )
            return;

        this._updateVisibilityInManifest(state.manifest, filterString, cloudArtifacts);
    }

    public getFilterString():string
    {
        const state = this._getState();
        return state.filterString;
    }

    @bind
    public _updateVisibilityInManifest(manifest:FlowManifest, filterString:string, cloudArtifacts:ResolvedCloudArtifact[]):void
    {
        const matchingComponents = this._getMatchingComponents(filterString, cloudArtifacts);
        if( !matchingComponents )
        {
            console.log('Error: could not compute matching flow components');
            return;
        }

        const idToMatches = new Map<CloudComponentId, any>();
        matchingComponents.map((mc:CloudComponent) =>
        {
            idToMatches.set(mc.id, true);
        });

        manifest.getAllComponents()
            .map(action((fc:FlowComponent) =>
            {
                const cloudComponentId = fc.component.id;

                if( !idToMatches.has(cloudComponentId) )
                    fc.isVisible = false;
                else
                    fc.isVisible = true;

                if( fc.isVisible )
                    fc.section.isExpanded = true;
            }));
    }

    @bind
    public _computeInitialManifest(cloudArtifacts:ResolvedCloudArtifact[]):FlowManifest
    {
        const filterString:string | null = this.getFilterString();
        const matchingComponents = this._getMatchingComponents(filterString, cloudArtifacts);

        const manifest:FlowManifest = new FlowManifest();
        matchingComponents.map((matchingComponent:CloudComponent) =>
        {
            this._addToManifest(manifest, matchingComponent);
        });

        return manifest;
    }

    @bind
    private _addToManifest(manifest:FlowManifest, c:CloudComponent)
    {
        let artifact:FlowArtifact = manifest.artifacts.find((a:FlowArtifact) => (a.artifactName == c.group.artifactName));
        if( !artifact )
        {
            artifact = new FlowArtifact(c.group.artifactName);
            manifest.artifacts.push(artifact);
        }

        let section = artifact.sections.find((s:FlowSection) => (s.sectionName === c.group.sectionName));
        if( !section )
        {
            section = new FlowSection(c.group.sectionName, artifact.artifactName);
            artifact.sections.push(section);
        }

        let component = section.components.find((fc:FlowComponent) => (fc.component.id === c.id));
        if( !component )
        {
            const newFC = new FlowComponent(section, c);

            section.components.push(newFC);
            manifest.registerComponent(newFC);
        }
    }

    @bind
    private _getMatchingComponents(filterString:string | null, cloudArtifacts:ResolvedCloudArtifact[]):CloudComponent[]
    {
        if( !cloudArtifacts )
            return null;

        const matchingComponents:CloudComponent[] = [];
        cloudArtifacts.map((rca:ResolvedCloudArtifact) =>
        {
            rca.components.map((c:CloudComponent) =>
            {
                if( c.name !== OUTPUT_COMPONENT_NAME && c.name !== ENTRY_COMPONENT_NAME )
                {
                    if( filterString )
                    {
                        const displayName = NameUtil.getDisplayName(c);
                        const lcDisplayName = displayName.toLowerCase();
                        const lcFilterString = filterString.toLowerCase();

                        if( lcDisplayName.indexOf(lcFilterString) !== -1 )
                            matchingComponents.push(c);
                    }
                    else
                        matchingComponents.push(c);
                }
            });
        });

        return matchingComponents;
    }

    private _getState():FlowComponentsPanelState
    {
        return rootState.ui.visualDesigner.workbench.panels.flow.components;
    }
}
